const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1"]);

const isLoopbackHostname = (hostname: string) => LOOPBACK_HOSTNAMES.has(hostname);

const stripApiSuffix = (url: string) => url.replace(/\/api\/?$/i, "").replace(/\/$/, "");

const deriveOriginFromWebUrl = (): string | null => {
  const webUrl = process.env.REACT_APP_WEB_URL;
  if (!webUrl) return null;

  try {
    const parsed = new URL(webUrl);
    const publicPort = process.env.REACT_APP_WEBSITE_PAGES_PUBLIC_PORT || "4999";
    parsed.port = publicPort;
    parsed.pathname = "";
    parsed.search = "";
    parsed.hash = "";
    // Lith local sirve HTTP; evita mezclar https del front con un API sin TLS en dev.
    if (
      parsed.protocol === "https:" &&
      (isLoopbackHostname(parsed.hostname) || /\.wembii\.com$/i.test(parsed.hostname))
    ) {
      parsed.protocol = "http:";
    }
    return parsed.origin;
  } catch {
    return null;
  }
};

/** Origen público del API Main para páginas web (preview / publicadas). */
export const getWebsitePagesPublicOrigin = (): string => {
  const explicit = process.env.REACT_APP_WEBSITE_PAGES_PUBLIC_ORIGIN?.trim();
  if (explicit) return stripApiSuffix(explicit);

  const mainUrl = stripApiSuffix(process.env.REACT_APP_MAIN_URL || "http://localhost:4999/api");
  const mainIsLoopback = (() => {
    try {
      return isLoopbackHostname(new URL(mainUrl).hostname);
    } catch {
      return /localhost|127\.0\.0\.1/i.test(mainUrl);
    }
  })();

  if (typeof window !== "undefined") {
    const browserHost = window.location.hostname;
    const browserIsLoopback = isLoopbackHostname(browserHost);

    if (mainIsLoopback && !browserIsLoopback) {
      const fromWebUrl = deriveOriginFromWebUrl();
      if (fromWebUrl) return fromWebUrl;
      const portMatch = mainUrl.match(/:(\d+)/);
      const apiPort = portMatch?.[1] || "4999";
      return `http://${browserHost}:${apiPort}`;
    }
  }

  if (mainIsLoopback) {
    const fromWebUrl = deriveOriginFromWebUrl();
    if (fromWebUrl) return fromWebUrl;
  }

  return mainUrl;
};

const apiBase = () => `${getWebsitePagesPublicOrigin()}/api`;

/** Reescribe localhost/127.0.0.1 cuando la app se abre desde otro dispositivo (ej. iPhone en dev). */
export const rewriteLocalhostPageUrl = (url: string): string => {
  if (typeof window === "undefined") return url;

  try {
    const parsed = new URL(url, window.location.origin);
    if (!isLoopbackHostname(parsed.hostname)) return url;

    const browserHost = window.location.hostname;
    if (isLoopbackHostname(browserHost)) return url;

    const publicOrigin = getWebsitePagesPublicOrigin();
    return `${publicOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

export const encodeCompanyId = (companyId: string) => btoa(String(companyId));

export const getPublishedPageUrl = (companyId: string, slug: string) =>
  `${apiBase()}/public/website/p/${encodeCompanyId(companyId)}/${slug}`;

export const getPreviewPageUrl = (companyId: string, pageId: string) =>
  `${apiBase()}/public/website/preview/${encodeCompanyId(companyId)}/${pageId}`;

export const resolvePublicPageUrl = (publicUrl: string) => {
  const absolute = publicUrl.startsWith("http")
    ? publicUrl
    : `${window.location.origin}${publicUrl}`;
  return rewriteLocalhostPageUrl(absolute);
};
